import * as React from "react"
import { connect } from "react-redux"

import { ActionAnchor, ActionType } from "@tenforce/toolbox-button-push-react"
import {
    TableBody,
    Content,
    Cell,
    Expander,
    CellType,
    Row,
    RowContentCell,
} from "@tenforce/toolbox-table-react"

import { GlobalState } from "../../../../../../store"
import { NavigationWorkspaces, getNavigationWorkspaces } from "../../selectors"
import {
    INavigationWorkspaceGroup,
    IWorkspaces as IWorkspaces,
} from "./../../index"
import { mapDispatchToProps, DispatchProps } from "../../actions"

import LoadMore from "../LoadMore"
import NavigationTab from "../NavigationTab"
import WorkspaceChildren from "./WorkspaceChildren"

interface OwnProps {
    nothingFoundLabel: string
    loadMoreLabel: string
    onNavigate: () => void
}

interface StateProps extends IWorkspaces, NavigationWorkspaces {}

type Props = OwnProps & StateProps & DispatchProps

interface State {
    limit: number
}

export class Workspaces extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = {
            limit: props.pageSize,
        }
    }

    render(): JSX.Element {
        const {
            filter,
            hasMore,
            isLoading,
            loadMoreLabel,
            navigationWorkspaces,
            nothingFoundLabel,
            pageSize,
            onNavigate,
        } = this.props

        return (
            <NavigationTab
                isLoading={isLoading}
                hasContent={navigationWorkspaces.length > 0}
                nothingFoundLabel={nothingFoundLabel}
                data-ft-id="navigationPanelWorkspaces"
            >
                <TableBody extraClasses="tw-mx-2">
                    {navigationWorkspaces.map(
                        (workspace: INavigationWorkspaceGroup) => {
                            const { id, label, url, workspaceChildren } =
                                workspace
                            const hasChildren = workspaceChildren.length > 0

                            const workspaceAnchor = (
                                <ActionAnchor
                                    href={url}
                                    title={label}
                                    onClick={onNavigate}
                                    extraClasses={`text-black ${
                                        hasChildren ? "tw-px-1" : "tw-px-3"
                                    }`}
                                    data-ft-component="workspaceLink"
                                    actionType={ActionType.block}
                                >
                                    <div
                                        className="label tw-truncate"
                                        data-ft-component="workspaceTitle"
                                    >
                                        {label}
                                    </div>
                                </ActionAnchor>
                            )

                            return hasChildren ? (
                                <Row
                                    key={id}
                                    isDefaultExpanded={filter.length > 0}
                                    data-ft-workspace={label.toLowerCase()}
                                    data-ft-component="workspaceRow"
                                >
                                    <Content>
                                        <Cell size="0" type={CellType.action}>
                                            <Expander data-ft-component="navigationExpander" />
                                        </Cell>
                                        <Cell type={CellType.action}>
                                            {workspaceAnchor}
                                        </Cell>
                                    </Content>

                                    <TableBody data-ft-component="navigationChildren">
                                        <WorkspaceChildren
                                            workspaceChildren={
                                                workspaceChildren
                                            }
                                            loadMoreLabel={loadMoreLabel}
                                            pageSize={pageSize}
                                            onNavigate={onNavigate}
                                        />
                                    </TableBody>
                                </Row>
                            ) : (
                                <RowContentCell
                                    key={id}
                                    data-ft-workspace={label.toLowerCase()}
                                    data-ft-component="workspaceRow"
                                    cellProps={{ type: CellType.action }}
                                >
                                    {workspaceAnchor}
                                </RowContentCell>
                            )
                        },
                    )}
                    {hasMore && (
                        <LoadMore
                            loadMoreLabel={loadMoreLabel}
                            loadMore={this.loadMore}
                            data-ft-load-more="navigationWorkspaces"
                        />
                    )}
                </TableBody>
            </NavigationTab>
        )
    }

    loadMore = (): void => {
        const { requestWorkspacesFull } = this.props
        requestWorkspacesFull()
    }
}

const mapStateToProps = (state: GlobalState): StateProps => ({
    ...state.navigation.workspaces,
    navigationWorkspaces: getNavigationWorkspaces(state.navigation),
})

export default connect(mapStateToProps, mapDispatchToProps)(Workspaces)
